import { useMemo } from "react";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { Check, PanelsTopLeft } from "lucide-react";

import apiClient from "../../../api-client";
import { Button } from "../../../components/Button";
import { useAuth } from "../../../components/contexts/AuthContext";
import { ArrowCarousel } from "./ArrowCarousel";
import { ContentPreviewCard, cardShellSx } from "./ContentPreviewCard";
import { WorkflowStepper } from "./WorkflowStepper";
import { getPieceWorkflow } from "./workflowUtils";

const pieceStatus = (status) => {
  if (status === "client_approved") return { label: "Aprobado", color: "info" };
  if (status === "client_review") return { label: "Pendiente", color: "pending" };
  return { label: "Borrador", color: "muted" };
};

/**
 * Approval of a whole batch (content group) for the client: every piece as a
 * card filling the row (arrows when they don't fit), a link to each piece's
 * detail to review and ask for changes, and one approval for the batch.
 */
export function GroupApprovalBoard({
  group,
  pieces,
  isAccepted,
  isAccepting,
  onAcceptGroup,
  onOpenPiece,
  onOpenImage,
  showStepper = true, // the client doesn't see the workflow steps
}) {
  const { interceptorReady } = useAuth();

  const detailQueries = useQueries({
    queries: pieces.map((piece) => ({
      queryKey: ["getData", `content-generation/pieces/${piece.id}/`, [piece.id]],
      queryFn: () => apiClient.get(`content-generation/pieces/${piece.id}/`).then((res) => res.data),
      enabled: interceptorReady,
      staleTime: 30 * 1000,
    })),
  });

  const details = detailQueries.map((query) => query.data || null);
  const isLoading = detailQueries.some((query) => query.isLoading);
  const groupStatus = isAccepted ? "client_approved" : group?.status || "client_review";

  const workflow = useMemo(
    () =>
      getPieceWorkflow(
        { status: groupStatus, has_platform_adaptations: true, platform_adaptations: [] },
        { isClient: true }
      ),
    [groupStatus]
  );
  const steps = workflow.steps.map((step) =>
    step.key === "generation" ? { ...step, subtitle: `${pieces.length} piezas creadas` } : step
  );

  const canRequest = groupStatus === "client_review";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showStepper && <WorkflowStepper steps={steps} selectedKey={workflow.current} />}

      {pieces.length === 0 ? (
        <Typography sx={{ color: "#6B7280", py: 4, textAlign: "center" }}>Esta tanda no tiene contenidos.</Typography>
      ) : (
        <ArrowCarousel ariaLabel="Piezas de la tanda" minItemWidth={340} itemSx={{ alignItems: "stretch" }}>
          {pieces.map((piece, index) => {
            const detail = details[index];
            if (!detail) {
              return (
                <Box key={piece.id} sx={{ ...cardShellSx, p: 2, gap: 1.5 }}>
                  <Skeleton variant="rounded" height={36} />
                  <Skeleton variant="rounded" height={140} />
                  <Skeleton variant="rounded" height={120} />
                </Box>
              );
            }
            return (
              <ContentPreviewCard
                key={piece.id}
                title={detail.title}
                adaptations={detail.platform_adaptations || []}
                status={pieceStatus(groupStatus)}
                onOpenImage={onOpenImage}
                onReadMore={() => onOpenPiece(piece.id)}
                textLines={6}
                actions={
                  <Button variant="outline" onClick={() => onOpenPiece(piece.id)} sx={{ flex: 1 }}>
                    <PanelsTopLeft size={16} />
                    {canRequest ? "Ver detalles y pedir cambios" : "Ver detalles"}
                  </Button>
                }
              />
            );
          })}
        </ArrowCarousel>
      )}

      {!isAccepted && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onAcceptGroup} disabled={isAccepting || isLoading}>
            {isAccepting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Check size={16} />}
            Aprobar {pieces.length} {pieces.length === 1 ? "pieza" : "piezas"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
